/**
 * Module Studio (UPT-2079 lot L2, §4.3) — squelette d'une spec SANS IA, même
 * gabarit que `SpecSkeleton.build` du serveur (rest §4.6) : les dix sections de
 * `_shared/README.md` remplies des faits connus du dossier (Module Spec,
 * prototypes importés et leur inventaire, contrepartie). Les chaînes venant du
 * Module Spec et des inventaires sont du texte : tout « ` » devient « ' » et
 * les retours à la ligne une espace (un titre ne casse jamais la structure).
 * Fonction pure ; la sortie passe par `normalizeSpecContent`.
 */
import type {
  DesignDossier,
  DesignModuleSpecJson,
  DesignSpecDocument,
  DesignSpecKind,
  DesignSpecStatus,
  DossierStatus,
  PrototypeInventory,
} from '../data/contracts';
import { normalizeSpecContent } from './specContent';

export interface SkeletonPrototype {
  title: string;
  toolName: string;
  pieceCount: number;
  inventory: PrototypeInventory | null;
}

export interface SkeletonInput {
  dossier: DesignDossier;
  document: Pick<DesignSpecDocument, 'kind' | 'title' | 'ticketRef' | 'status'>;
  moduleSpec: DesignModuleSpecJson | null;
  /** Imports de prototypes du dossier non archivés. */
  prototypes: SkeletonPrototype[];
  /** Chemin du document de l'autre type portant le même ticket, s'il existe. */
  counterpartPath: string | null;
  /** Date du jour au format JJ.MM.AAAA. */
  date: string;
}

const NONE = '—';
const TODO = 'À compléter.';
const UNKNOWN_TICKET = 'UPT-?';

/** Date au format JJ.MM.AAAA (fuseau local). */
export const formatSkeletonDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
};

/** Texte sûr pour le Markdown : apostrophe à la place du « ` », retours à la ligne aplatis. */
const text = (value: unknown): string =>
  String(value ?? '').replace(/`/g, "'").replace(/[\r\n]+/g, ' ');

const targetRepository = (kind: DesignSpecKind): string => {
  switch (kind) {
    case 'rest':
      return 'Geode-rest-v3';
    case 'web':
      return 'Geode-web-v3';
    default:
      return 'Geode-specs';
  }
};

const specStatusText = (status: DesignSpecStatus): string => {
  switch (status) {
    case 'attribuee':
      return 'attribuée';
    case 'en-cours':
      return 'en cours';
    case 'livree':
      return 'livrée';
    case 'integree':
      return 'intégrée';
    default:
      return 'libre';
  }
};

const dossierStatusText = (status: DossierStatus): string => {
  switch (status) {
    case 'idea':
      return 'Idée';
    case 'design':
      return 'Conception';
    case 'spec':
      return 'Spec';
    case 'preview':
      return 'Preview';
    case 'generated':
      return 'Généré';
    case 'dev':
      return 'DEV';
    case 'uat':
      return 'UAT';
    case 'prod':
      return 'PROD';
    default:
      return 'Archivé';
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const entityNames = (moduleSpec: DesignModuleSpecJson | null): string[] =>
  moduleSpec && isRecord(moduleSpec.entities) ? Object.keys(moduleSpec.entities) : [];

const fieldCount = (entity: unknown): string => {
  if (isRecord(entity) && Array.isArray(entity.fields)) {
    return String(entity.fields.length);
  }
  return '?';
};

/** Markdown du squelette (LF), exactement la forme de rest §4.6. */
export function buildSpecSkeleton(input: SkeletonInput): string {
  const { dossier, document, moduleSpec, prototypes, counterpartPath, date } = input;
  const ticket = document.ticketRef ?? UNKNOWN_TICKET;
  const tickets = dossier.ticketRefs.length > 0 ? dossier.ticketRefs.join(', ') : NONE;
  const names = entityNames(moduleSpec);

  const moduleSpecLine = moduleSpec
    ? `- Module Spec : version ${dossier.moduleSpecVersion} (${names.length} entités : ${names
        .map((name) => `\`${text(name)}\``)
        .join(', ')}).`
    : '- Module Spec : aucun Module Spec déposé.';

  const prototypeLines =
    prototypes.length > 0
      ? [
          '- Prototypes importés :',
          ...prototypes.map((prototype) => {
            const screens = prototype.inventory?.screens.length ?? 0;
            return `- ${text(prototype.title)} (${text(prototype.toolName)}, ${prototype.pieceCount} pièces, ${screens} écrans inventoriés)`;
          }),
        ]
      : ['- Prototypes importés : aucun.'];

  const entityLines =
    names.length > 0 && moduleSpec
      ? names.map((name) => `- \`${text(name)}\` : ${fieldCount(moduleSpec.entities[name])} champs`)
      : [TODO];

  const screenLines = prototypes.flatMap((prototype) =>
    (prototype.inventory?.screens ?? []).map(
      (screen) =>
        `- \`${text(screen.label)}\` : ${screen.headings.length} titres, ${screen.forms.length} formulaires, ${screen.tables.length} tableaux, ${screen.actions.length} actions`
    )
  );

  const lines = [
    `# SPEC-${ticket} (${document.kind}) — ${text(document.title)}`,
    '',
    `> Dépôt cible : \`${targetRepository(document.kind)}\` · Branche : à fixer par le CORE · Statut : ${specStatusText(document.status)} ·`,
    `> Contrepartie : \`${counterpartPath ?? NONE}\` · Dossier : ${text(dossier.code)} — ${text(dossier.name)} · Ticket Paperclip ${document.ticketRef ?? NONE} ·`,
    `> Généré le ${date} par Module Studio (squelette sans IA, à relire)`,
    '',
    '## 1. Pourquoi',
    dossier.summary.trim() ? text(dossier.summary) : TODO,
    '',
    '## 2. Périmètre et hypothèses',
    `- Module : \`${dossier.moduleCode ?? 'à définir'}\` · étape du dossier : ${dossierStatusText(dossier.status)} · tickets : ${tickets}.`,
    moduleSpecLine,
    ...prototypeLines,
    `- ${TODO}`,
    '',
    '## 3. Modèle de données',
    ...entityLines,
    '',
    '## 4. API',
    TODO,
    '',
    '## 5. Web',
    ...(screenLines.length > 0 ? screenLines : [TODO]),
    '',
    '## 6. Sécurité, droits, données personnelles',
    'À compléter (droits `DESIGN_*` ou entité du module, RLS, données personnelles tokenisées).',
    '',
    '## 7. Tests attendus',
    TODO,
    '',
    '## 8. Critères d\'acceptation et recette',
    TODO,
    '',
    '## 9. Livraison',
    `À compléter (dépôt, branche, migrations, rapport \`reports/${ticket}-${document.kind}.md\`).`,
    '',
    '## 10. Références',
    '`_shared/README.md`, `_shared/CONTEXT.md`, `web/SPEC-UPT-2079.md`, `contracts/module-studio/contracts.ts`.',
    '',
  ];
  return normalizeSpecContent(lines.join('\n'));
}
